import { Button, ButtonGroup, Stack, Text, Textarea } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { Feature, FeatureCollection, Position } from "geojson";
import { totalLineStringMeters } from "../../../../../map/utils/map-utils";
import { useStores } from "../../../../../shared/stores/RootStore";
import { toast } from "../../../../../shared/views/CustomToaster";
import { UpdateOsaWay } from "../../../../../osaway/models/OsaWay";
import { DeleteIcon, EditIcon } from "../../../../../shared/views/icons";

const i18prefix = "map:designStage";

// these variables are kept updated without causing re-renders (useful for the prompt)
var isDirty = false;
var updatedSubmitFunction = () => {};

const DesignStageControlsBase: React.FC = () => {
  const { osaWayStore, mapStore } = useStores();

  const way = osaWayStore.getSelected()!;

  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  isDirty =
    way.description !== description ||
    way.plannedTrajectory !== mapStore.newPlannedTrajectory;

  useEffect(() => {
    if (way?.permissions.canUpdate) {
      mapStore.plannedTrajectoryMapSource?.configDrawControl(setSelectedIds);
    }

    setDescription(way.description || "");

    return () => {
      mapStore.plannedTrajectoryMapSource?.clearDrawControl();
    };
    // eslint-disable-next-line
  }, [way.id]);

  /**
   * Prompt to save before leaving
   */
  useEffect(() => {
    return () => {
      if (isDirty && window.confirm(t(`${i18prefix}.submitBeforeLeaving`))) {
        updatedSubmitFunction();
      }
    };
    // eslint-disable-next-line
  }, []);

  const submit = async () => {
    setSubmitting(true);
    const request: UpdateOsaWay = {
      name: way.name,
      description,
      plannedTrajectory: mapStore.newPlannedTrajectory,
      plannedDistance: mapStore.newPlannedTrajectory
        ? totalLineStringMeters(mapStore.newPlannedTrajectory)
        : 0,
    };
    await osaWayStore.updateOsaWay(way!.id, request).then(() => {
      toast("success", t(`${i18prefix}.submitSuccess`));
      setSubmitting(false);
    });
  };

  updatedSubmitFunction = submit;

  const clickImportKml = () => {
    const input = document.getElementById(kmlInputId);
    input?.click();
  };

  const submitKml = (content: string) => {
    const drawControl = mapStore.plannedTrajectoryMapSource?.drawControl;
    if (drawControl) {
      const newGeoJson = kmlToGeoJson(content);
      // delete previous lines
      const oldGeoJson = drawControl!.getAll();
      for (const feat of oldGeoJson.features) {
        drawControl!.delete(feat.id!.toString());
      }
      // add new lines
      drawControl!.add(newGeoJson);
      mapStore.setNewPlannedTrajectory(newGeoJson);
    }
  };

  const reset = () => {
    mapStore.plannedTrajectoryMapSource?.clearDrawControl();
    mapStore.plannedTrajectoryMapSource?.configDrawControl(setSelectedIds);
    setDescription(way.description || "");
  };

  return (
    <Stack py={2} gap={4}>
      <Stack>
        <Text>{t(`${i18prefix}.trajectoryLabel`)}</Text>

        <ButtonGroup
          size="sm"
          fontSize={"sm"}
          variant={"surface"}
          colorPalette={"primary"}
          flexDir={"column"}
          alignItems={"stretch"}
        >
          <Button
            onClick={() => {
              const drawControl =
                mapStore.plannedTrajectoryMapSource?.drawControl;
              if (drawControl) {
                drawControl.changeMode("draw_line_string");
              }
            }}
          >
            <EditIcon /> Draw line
          </Button>

          <Button
            colorPalette={"red"}
            disabled={!selectedIds.length}
            onClick={() => {
              const drawControl =
                mapStore.plannedTrajectoryMapSource?.drawControl;
              if (drawControl) {
                drawControl.trash();
                setSelectedIds([]);
              }
            }}
          >
            <DeleteIcon /> Delete selected
          </Button>

          <Button onClick={clickImportKml} variant={"ghost"}>
            {t(`${i18prefix}.importKml`)}
          </Button>
        </ButtonGroup>
      </Stack>

      <input
        id={kmlInputId}
        accept=".kml"
        type="file"
        style={fileInputStyle}
        onChange={(e) => {
          const [file] = e.target.files!;
          if (file) {
            file.text().then(submitKml);
          }
        }}
      />

      <Text>{t(`${i18prefix}.descriptionLabel`)}</Text>
      <Textarea
        fontSize="sm"
        value={description}
        placeholder="Message for the data collector"
        onChange={(e) => setDescription(e.target.value)}
        disabled={!way?.permissions.canUpdate}
      />

      <ButtonGroup
        colorPalette="primary"
        variant="ghost"
        size="sm"
        fontSize={"sm"}
        justifyContent={"end"}
      >
        <Button onClick={reset}>Cancel</Button>
        <Button
          onClick={submit}
          variant={"solid"}
          disabled={!isDirty}
          loading={submitting}
        >
          {t(`${i18prefix}.saveButton`)}
        </Button>
      </ButtonGroup>
    </Stack>
  );
};

export const DesignStageControls = observer(DesignStageControlsBase);

const kmlInputId = "kmlInput";
const fileInputStyle = {
  position: "absolute",
  top: 0,
  right: 0,
  width: 1,
  height: 1,
  opacity: 0.1,
} as any;

const kmlCoordsRegex = /-{0,1}\d{1,3}\.\d+,-{0,1}\d{1,2}\.\d+/g;

const kmlToGeoJson = (text: string) => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(text, "text/xml");
  const placemarks = xmlDoc.getElementsByTagName("Placemark");

  const features: Feature[] = [];

  for (const p of placemarks) {
    const allCoords = p.getElementsByTagName("coordinates");
    if (allCoords[0]?.textContent) {
      const lines = allCoords[0].textContent.matchAll(kmlCoordsRegex);
      const featureCoords: Position[] = [];
      for (const line of lines) {
        const [lng, lat] = line[0].split(",").map((str) => parseFloat(str));
        featureCoords.push([lng, lat]);
      }
      features.push({
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          coordinates: featureCoords,
        },
      });
    }
  }

  return { type: "FeatureCollection", features: features } as FeatureCollection;
};
